// Message markdown renderer shared by the timeline, thread panel and
// composer preview. `marked` only lexes; tokens become React nodes here,
// so no message HTML is ever injected. Type size, colour and weight
// inherit from the container; layout lives in `.chat-md` (styles/markdown.css).

import { Check, Square } from "lucide-react";
import { Lexer, Tokenizer, type Token, type Tokens } from "marked";
import { Fragment, useContext, type MouseEvent, type ReactNode } from "react";

import { CodeBlock } from "~/components/CodeBlock";
import { NurionLinkChip } from "~/components/NurionLinkChip";
import { matrixLocalpart } from "~/lib/display-name";
import { parseNurionLink } from "~/lib/nurion-links";
import { openExternal, platform } from "~/lib/platform";
import { parseRoomPermalink, RoomLinkContext, type OpenRoomLink } from "~/lib/permalink";
import { ICON_STROKE } from "~/lib/icon";

/**
 * Link protocol allow-list: only `http:`/`https:` become clickable, so a
 * `javascript:` or `data:` href in a message renders as plain text.
 */
export function sanitizeLinkHref(href: string): string | null {
  try {
    const url = new URL(href);
    if (url.protocol === "http:" || url.protocol === "https:") return url.toString();
    return null;
  } catch {
    return null;
  }
}

export function shortLinkLabel(href: string): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return href;
  }
  const host = url.hostname.startsWith("www.") ? url.hostname.slice(4) : url.hostname;
  const path = url.pathname.replace(/^\/+/, "").replace(/\/+$/, "");
  if (!path) return host;
  const segments = path.split("/");
  const last = segments.at(-1) ?? "";
  const trimmed = last.length > 24 ? `${last.slice(0, 23)}…` : last;
  return segments.length > 1 ? `${host}/…/${trimmed}` : `${host}/${trimmed}`;
}

/**
 * The desktop and iOS webviews ignore `target="_blank"`, so a tap on a
 * link does nothing there. In the app, hand the address to the system
 * browser; on the web the anchor does its own job.
 */
export function openInSystemBrowser(href: string) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (platform !== "tauri" || event.defaultPrevented) return;
    if (event.button !== 0) return;
    event.preventDefault();
    void openExternal(href).catch((err) => console.warn("openExternal failed", err));
  };
}

/** A display-name span the sender's client linked to a Matrix user. */
export interface MessageMention {
  text: string;
  userId: string;
}

// Chat, not documents: a line followed by `---` is text plus a rule, not a
// setext heading, and pasted indented text stays text rather than code.
class ChatTokenizer extends Tokenizer {
  override lheading(): undefined {
    return undefined;
  }
  override code(): undefined {
    return undefined;
  }
}

const FENCE_RE = /^\s*```/;
const ONE_LINE_FENCE_RE = /^(\s*)```([^`]+)```\s*$/;
// The attribution line `formatQuote` appends: `— Name, [link to event](https://matrix.to/…)`.
const QUOTE_ATTRIBUTION_RE = /^— (.+), \[link to event\]\(https:\/\/matrix\.to\/#\/[^)\s]+\)$/;
// Legacy Matrix reply fallback: the first quoted line starts with `<@user:server> `.
const REPLY_FALLBACK_RE = /^<(@[^>\s]+:[^>\s]+)> ?/;

/** Expand chat-style one-line fences (```` ```x``` ````) into real fences. */
function expandOneLineFences(input: string): string {
  let inFence = false;
  return input
    .split("\n")
    .flatMap((line) => {
      const oneLine = inFence ? null : ONE_LINE_FENCE_RE.exec(line);
      if (oneLine) return [`${oneLine[1]}\`\`\``, oneLine[2] ?? "", `${oneLine[1]}\`\`\``];
      if (FENCE_RE.test(line)) inFence = !inFence;
      return [line];
    })
    .join("\n");
}

export function lexMessage(text: string): Token[] {
  return new Lexer({ gfm: true, breaks: true, tokenizer: new ChatTokenizer() }).lex(
    expandOneLineFences(text),
  );
}

interface QuoteParts {
  tokens: Token[];
  author: string | null;
  authorId: string | null;
}

function splitQuote(token: Tokens.Blockquote, isFirst: boolean): QuoteParts {
  const lines = token.text.split("\n");
  const attribution = QUOTE_ATTRIBUTION_RE.exec(lines[lines.length - 1] ?? "");
  if (attribution) {
    return {
      tokens: lexMessage(lines.slice(0, -1).join("\n")),
      author: attribution[1] ?? null,
      authorId: null,
    };
  }
  const fallback = isFirst ? REPLY_FALLBACK_RE.exec(lines[0] ?? "") : null;
  if (fallback) {
    lines[0] = (lines[0] ?? "").slice(fallback[0].length);
    return { tokens: lexMessage(lines.join("\n")), author: null, authorId: fallback[1] ?? null };
  }
  return { tokens: token.tokens, author: null, authorId: null };
}

interface RenderContext {
  mentions: readonly MessageMention[];
  resolveName: (userId: string) => string;
  /** The paragraph that ends the message, which carries `trailing` inline. */
  tailParagraph: Token | null;
  trailing: ReactNode;
  onOpenRoomLink: OpenRoomLink | null;
}

function renderMentions(text: string, ctx: RenderContext, key: string): ReactNode {
  const hit = ctx.mentions.find((mention) => mention.text && text.includes(mention.text));
  if (!hit) return text;
  const at = text.indexOf(hit.text);
  return (
    <Fragment key={key}>
      {text.slice(0, at)}
      <span className="chat-md-mention" title={hit.userId}>
        {hit.text}
      </span>
      {renderMentions(text.slice(at + hit.text.length), ctx, `${key}r`)}
    </Fragment>
  );
}

function renderInline(tokens: Token[] | undefined, ctx: RenderContext): ReactNode[] {
  return (tokens ?? []).map((token, i) => {
    const key = String(i);
    switch (token.type) {
      case "strong":
        return <strong key={key}>{renderInline(token.tokens, ctx)}</strong>;
      case "em":
        return <em key={key}>{renderInline(token.tokens, ctx)}</em>;
      case "del":
        return <del key={key}>{renderInline(token.tokens, ctx)}</del>;
      case "codespan":
        return (
          <code key={key} className="chat-md-code">
            {token.text}
          </code>
        );
      case "br":
        return <br key={key} />;
      case "link": {
        const href = sanitizeLinkHref(token.href);
        if (!href) return <Fragment key={key}>{token.text}</Fragment>;
        const open = ctx.onOpenRoomLink;
        const permalink = open ? parseRoomPermalink(href) : null;
        const isBare = token.text === token.href;
        const nurion = isBare ? parseNurionLink(href) : null;
        const children = nurion ? (
          <NurionLinkChip link={nurion} />
        ) : isBare ? (
          shortLinkLabel(href)
        ) : (
          renderInline(token.tokens, ctx)
        );
        const ariaLabel = nurion
          ? `${nurion.product} ${nurion.label}: ${href}`
          : isBare
            ? href
            : undefined;
        return (
          <a
            key={key}
            className="chat-message-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={
              permalink && open
                ? (event) => {
                    if (event.button !== 0) return;
                    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                    if (open(permalink)) {
                      event.preventDefault();
                      return;
                    }
                    openInSystemBrowser(href)(event);
                  }
                : openInSystemBrowser(href)
            }
            data-nurion-link={nurion ? nurion.kind : undefined}
            title={isBare ? href : undefined}
            aria-label={ariaLabel}
          >
            {children}
          </a>
        );
      }
      // Remote images would leak the reader's IP to any host; show a link.
      case "image": {
        const href = sanitizeLinkHref(token.href);
        const label = token.text || token.href;
        if (!href) return <Fragment key={key}>{label}</Fragment>;
        return (
          <a
            key={key}
            className="chat-message-link"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={openInSystemBrowser(href)}
          >
            {label}
          </a>
        );
      }
      case "text":
        return "tokens" in token && token.tokens ? (
          <Fragment key={key}>{renderInline(token.tokens, ctx)}</Fragment>
        ) : (
          <Fragment key={key}>{renderMentions(token.text, ctx, key)}</Fragment>
        );
      case "escape":
      case "html":
        return <Fragment key={key}>{token.text}</Fragment>;
      default:
        return <Fragment key={key}>{"raw" in token ? String(token.raw) : null}</Fragment>;
    }
  });
}

function renderListItem(item: Tokens.ListItem, ctx: RenderContext, key: number): ReactNode {
  const body = item.tokens.filter((token) => token.type !== "checkbox");
  if (!item.task) return <li key={key}>{renderBlocks(body, ctx, false)}</li>;
  const Icon = item.checked ? Check : Square;
  return (
    <li key={key} className="chat-md-task" data-checked={item.checked ? "" : undefined}>
      <Icon
        className="chat-md-task-box"
        size={14}
        strokeWidth={ICON_STROKE}
        aria-label={item.checked ? "Done" : "Not done"}
      />
      <div>{renderBlocks(body, ctx, false)}</div>
    </li>
  );
}

function renderBlocks(tokens: Token[], ctx: RenderContext, topLevel: boolean): ReactNode[] {
  const out: ReactNode[] = [];
  tokens.forEach((token, i) => {
    switch (token.type) {
      case "space":
      case "def":
        return;
      case "paragraph":
        out.push(
          <p key={i}>
            {renderInline(token.tokens, ctx)}
            {token === ctx.tailParagraph && ctx.trailing}
          </p>,
        );
        return;
      // List-item text arrives as `text` blocks with inline children.
      case "text":
        out.push(
          <p key={i}>
            {token.tokens ? renderInline(token.tokens, ctx) : renderMentions(token.text, ctx, "t")}
          </p>,
        );
        return;
      case "heading": {
        const level = Math.min(token.depth, 3);
        out.push(
          <p
            key={i}
            className="chat-md-heading"
            data-level={level}
            role="heading"
            aria-level={level}
          >
            {renderInline(token.tokens, ctx)}
          </p>,
        );
        return;
      }
      case "blockquote": {
        const quote = splitQuote(token as Tokens.Blockquote, topLevel && out.length === 0);
        const author = quote.author ?? (quote.authorId ? ctx.resolveName(quote.authorId) : null);
        out.push(
          <blockquote key={i} className="chat-md-quote">
            {renderBlocks(quote.tokens, ctx, false)}
            {author && (
              <span className="chat-md-quote-author" data-quote-author="">
                {author}
              </span>
            )}
          </blockquote>,
        );
        return;
      }
      case "code":
        out.push(<CodeBlock key={i} code={token.text} lang={token.lang ?? null} />);
        return;
      case "list": {
        const list = token as Tokens.List;
        const items = list.items.map((item, j) => renderListItem(item, ctx, j));
        const isTaskList = list.items.some((item) => item.task);
        out.push(
          list.ordered ? (
            <ol key={i} className="chat-md-list" start={list.start === "" ? undefined : list.start}>
              {items}
            </ol>
          ) : (
            <ul key={i} className="chat-md-list" data-tasks={isTaskList ? "" : undefined}>
              {items}
            </ul>
          ),
        );
        return;
      }
      case "table": {
        const table = token as Tokens.Table;
        out.push(
          <div key={i} className="chat-md-table">
            <table>
              <thead>
                <tr>
                  {table.header.map((cell, j) => (
                    <th key={j} style={{ textAlign: cell.align ?? undefined }}>
                      {renderInline(cell.tokens, ctx)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, j) => (
                      <td key={j} style={{ textAlign: cell.align ?? undefined }}>
                        {renderInline(cell.tokens, ctx)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>,
        );
        return;
      }
      case "hr":
        out.push(<hr key={i} className="chat-md-rule" />);
        return;
      default:
        out.push(<p key={i}>{"raw" in token ? String(token.raw).trim() : null}</p>);
    }
  });
  return out;
}

export interface MarkdownBodyProps {
  text: string;
  /** Rendered inline after the last block (e.g. "(edited)"). */
  trailing?: ReactNode;
  mentions?: readonly MessageMention[];
  resolveName?: (userId: string) => string;
  /** Defaults to the shell's RoomLinkContext. */
  onOpenRoomLink?: OpenRoomLink;
}

export function MarkdownBody({
  text,
  trailing,
  mentions = [],
  resolveName,
  onOpenRoomLink,
}: MarkdownBodyProps) {
  const contextOpenRoomLink = useContext(RoomLinkContext);
  const tokens = lexMessage(text);
  const last = tokens.filter((token) => token.type !== "space").at(-1) ?? null;
  const tailParagraph = last?.type === "paragraph" ? last : null;
  const ctx: RenderContext = {
    mentions,
    resolveName: resolveName ?? matrixLocalpart,
    tailParagraph,
    trailing,
    onOpenRoomLink: onOpenRoomLink ?? contextOpenRoomLink,
  };
  return (
    <div className="chat-md">
      {renderBlocks(tokens, ctx, true)}
      {trailing && !tailParagraph && <p>{trailing}</p>}
    </div>
  );
}
